/**
 * Debug Mode Toggle Component
 *
 * A toggle switch that controls the global debug_mode_enabled config.
 * When debug mode is ON, generation functions use the debug model preset
 * instead of the production preset.
 *
 * The toggle is RED when active to make it visually prominent.
 * Only visible when VITE_ALLOW_DEBUG_MODE=true in the environment,
 * and never in production (#528) — prod must never use debug models.
 *
 * @module DebugModeToggle
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Switch,
  Typography,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { Bug } from 'lucide-react';
import { logger } from '@musedrops/shared';
import { ConfigApiService } from '../../services/configApi';
import { isProduction } from '../../config/env';

// Debug mode requires the opt-in env flag AND is hard-blocked in production
// (#528): even if VITE_ALLOW_DEBUG_MODE leaks into a prod build, hide it.
const isDebugModeAllowed =
  !isProduction() && import.meta.env.VITE_ALLOW_DEBUG_MODE === 'true';

/**
 * DebugModeToggle - Global debug mode switch for the application header
 */
export function DebugModeToggle() {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  // Fetch current debug mode state on mount
  useEffect(() => {
    // Skip fetching if debug mode is not allowed
    if (!isDebugModeAllowed) {
      setIsLoading(false);
      return;
    }

    async function fetchDebugMode() {
      try {
        const { config } = await ConfigApiService.getProcessConfig();
        setIsEnabled(config.debug_mode_enabled === true);
      } catch (error) {
        logger.error('Failed to fetch debug mode config:', {
          error: String(error),
        });
        setIsEnabled(false);
      } finally {
        setIsLoading(false);
      }
    }
    fetchDebugMode();
  }, []);

  // Handle toggle change
  const handleToggle = useCallback(async () => {
    const newValue = !isEnabled;
    setIsUpdating(true);

    try {
      await ConfigApiService.updateConfiguration(
        'debug_mode_enabled',
        newValue
      );
      setIsEnabled(newValue);
      // Dispatch custom event so other components can react to the change
      window.dispatchEvent(
        new CustomEvent('debugModeChanged', { detail: { isEnabled: newValue } })
      );
    } catch (error) {
      logger.error('Failed to update debug mode:', { error: String(error) });
      // Revert on error
    } finally {
      setIsUpdating(false);
    }
  }, [isEnabled]);

  // Don't render anything if debug mode is not allowed
  if (!isDebugModeAllowed) {
    return null;
  }

  // Show loading state
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', mx: 2 }}>
        <CircularProgress size={20} color="inherit" />
      </Box>
    );
  }

  return (
    <Tooltip
      title={
        isEnabled
          ? 'Debug Mode ON - Using debug model presets for generation'
          : 'Debug Mode OFF - Using production model presets'
      }
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 0.5,
          borderRadius: 2,
          backgroundColor: isEnabled
            ? 'rgba(244, 67, 54, 0.15)'
            : 'transparent',
          border: isEnabled
            ? '1px solid rgba(244, 67, 54, 0.5)'
            : '1px solid transparent',
          transition: 'all 0.2s ease-in-out',
        }}
      >
        <Bug
          size={18}
          color={isEnabled ? '#f44336' : 'inherit'}
          style={{ opacity: isEnabled ? 1 : 0.5 }}
        />
        <Typography
          variant="body2"
          sx={{
            fontWeight: isEnabled ? 600 : 400,
            color: isEnabled ? '#f44336' : 'inherit',
            fontSize: '0.875rem',
          }}
        >
          Debug Models
        </Typography>
        <Switch
          checked={isEnabled}
          onChange={handleToggle}
          disabled={isUpdating}
          size="small"
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: '#f44336',
            },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: '#f44336',
            },
          }}
        />
        {isUpdating && (
          <CircularProgress
            size={16}
            sx={{ color: isEnabled ? '#f44336' : 'inherit' }}
          />
        )}
      </Box>
    </Tooltip>
  );
}
